import { KINDS, kindOfId, mentionClass } from '../lib/model'
import { useMentionScope } from './Mentions'
import { Icon, iconName } from './Icon'

/**
 * The record's current name, or the label frozen into the mention token.
 *
 * Reads the mention SCOPE rather than the store: this renders inside the row card,
 * which the client's seat also draws, and the seat mounts no store — `useStore`
 * throws there rather than coming back empty. The fallback is why the token carries
 * a label at all: a scope that cannot see the record still renders something true
 * about what the writer meant.
 */
export function useEntityTitle(id: string, fallback = ''): string {
  const { title } = useMentionScope()
  const kind = kindOfId(id)
  return (
    title(id) || fallback || (kind ? `Untitled ${KINDS[kind].singular}` : 'Unknown')
  )
}

/**
 * Inline link to a record, used for mention chips and reference fields. The chip
 * takes its colour from the kind it points at — teal work, rust deals, violet orgs,
 * gold people — so a sentence full of mentions is scannable at a glance.
 */
export function EntityLink({
  id,
  label,
  showIcon = false,
}: {
  id: string
  label?: string
  showIcon?: boolean
}) {
  const { open } = useMentionScope()
  const title = useEntityTitle(id, label ?? '')
  const kind = kindOfId(id)
  // No `open` is the client's page: there are no record pages there, and a chip that
  // goes nowhere should not claim to be a link.
  return (
    <span
      className={mentionClass(kind ?? undefined)}
      {...(open ? { role: 'link', tabIndex: 0 } : {})}
      onClick={(e) => {
        if (!open) return
        e.stopPropagation()
        open(id)
      }}
      onKeyDown={(e) => {
        if (open && e.key === 'Enter') open(id)
      }}
    >
      {showIcon && kind && (
        <Icon name={iconName(KINDS[kind].icon)} size={12} className="mr-0.5 mb-0.5 inline" />
      )}
      {title}
    </span>
  )
}
